import { jsPDF } from 'jspdf';
import 'svg2pdf.js';
import { api } from './api';

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function downloadText(text, filename, mime = 'text/xml') {
  downloadBlob(new Blob([text], { type: mime }), filename);
}

function svgStringToElement(svg) {
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  return doc.documentElement;
}

// svg2pdf/jsPDF can only render the 3 built-in PDF font families
// (helvetica/courier/times). Our SVGs use 'IBM Plex Sans'/'IBM Plex Mono' which
// svg2pdf cannot resolve, so it falls back to a wrong (serif) font. Remap every
// font-family to the matching built-in family so the PDF keeps a clean
// sans-serif body and monospace for codes/IPs, matching the on-screen design.
function mapPdfFont(fam) {
  if (!fam) return 'helvetica';
  const f = String(fam).toLowerCase();
  if (f.includes('mono') || f.includes('courier') || f.includes('consol')) return 'courier';
  if (f.includes('times') || f.includes('georgia') || f.includes('playfair') || f.includes('crimson') || (f.includes('serif') && !f.includes('sans'))) return 'times';
  return 'helvetica';
}

function mapPdfWeight(w) {
  if (w == null || w === '') return null;
  const s = String(w).toLowerCase();
  if (s === 'bold' || s === 'bolder') return 'bold';
  if (s === 'normal' || s === 'lighter') return 'normal';
  const n = parseInt(s, 10);
  if (Number.isFinite(n)) return n >= 600 ? 'bold' : 'normal';
  return 'normal';
}

function remapFontsForPdf(root) {
  // svg2pdf resolves fonts via the computed/inline style (not the presentation
  // attribute) and only knows jsPDF's built-in families. Set BOTH the inline
  // style and the attribute, and normalise numeric weights to bold/normal, so
  // every text node renders in a correct built-in font instead of falling back
  // to Times.
  if (root.style) root.style.fontFamily = 'helvetica';
  const els = [root, ...root.querySelectorAll('*')];
  els.forEach((el) => {
    const famAttr = el.getAttribute && el.getAttribute('font-family');
    if (famAttr != null) {
      const mapped = mapPdfFont(famAttr);
      el.setAttribute('font-family', mapped);
      if (el.style) el.style.fontFamily = mapped;
    }
    const wAttr = el.getAttribute && el.getAttribute('font-weight');
    if (wAttr != null) {
      const mw = mapPdfWeight(wAttr);
      if (mw) {
        el.setAttribute('font-weight', mw);
        if (el.style) el.style.fontWeight = mw;
      }
    }
    if (el.style && el.style.fontFamily && !famAttr) {
      el.style.fontFamily = mapPdfFont(el.style.fontFamily);
    }
  });
}

// Vector PDF generated entirely in the browser.
export async function exportBrowserPDF(svgStrings, filename = 'network-diagram.pdf') {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  pdf.setFont('helvetica', 'normal');
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  for (let i = 0; i < svgStrings.length; i++) {
    if (i > 0) pdf.addPage('a3', 'landscape');
    const el = svgStringToElement(svgStrings[i]);
    remapFontsForPdf(el);
    // eslint-disable-next-line no-await-in-loop
    await pdf.svg(el, { x: 0, y: 0, width: pageW, height: pageH });
  }
  pdf.save(filename);
}

// High-quality vector PDF rendered server-side (cairosvg).
export async function exportServerPDF(pages, filename = 'network-diagram.pdf') {
  const res = await api.post('/pdf/generate', { filename, pages }, { responseType: 'blob' });
  downloadBlob(new Blob([res.data], { type: 'application/pdf' }), filename);
}

// Reliable print-to-PDF fallback (always vector). Builds the print window via
// DOM APIs instead of document.write, and triggers printing from the opener
// (no injected inline <script>). The SVG markup is internally generated by
// svgExport (all dynamic text is escaped), so assigning it as innerHTML is safe.
export function exportPrintPDF(svgStrings) {
  const w = window.open('', '_blank');
  if (!w) return false;
  const d = w.document;
  d.title = 'PDF';

  const style = d.createElement('style');
  style.textContent =
    '@page{size:A3 landscape;margin:0} html,body{margin:0;padding:0;background:#fff}' +
    '.pg{page-break-after:always;width:100%;display:flex;align-items:center;justify-content:center}' +
    'svg{width:100%;height:auto;display:block}';
  d.head.appendChild(style);

  svgStrings.forEach((s) => {
    const div = d.createElement('div');
    div.className = 'pg';
    div.innerHTML = s; // generated + escaped SVG markup
    d.body.appendChild(div);
  });

  // Give the popup a moment to lay out the SVGs, then print from the opener.
  setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch {
      /* popup may have been closed by the user */
    }
  }, 400);
  return true;
}
